import { type ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { graphqlRequest, portalApi } from "./api.ts";
import { Auth, identityOf } from "./auth.ts";
import { loadConfig } from "./config.ts";
import { Mark } from "./Icons.tsx";
import { appSyncFeed, pollingOnly } from "./live.ts";
import "./styles.css";
import { Button, Notice } from "./ui.tsx";

/**
 * Starts the portal: reads `config.json`, finishes a sign-in Cognito redirected back from, and shows
 * the pages the signed-in user's role allows. On the dev server with a local data API, the
 * development token stands in for the sign-in.
 */
async function start(): Promise<void> {
  const element = document.getElementById("root");
  if (!element) return;
  const root = createRoot(element);
  const render = (node: ReactNode) => root.render(<StrictMode>{node}</StrictMode>);

  try {
    const config = await loadConfig();
    // AppSync's subscriptions; the local data API serves none, so the pages poll there.
    const liveFeed = (token: () => Promise<string | undefined>) =>
      appSyncFeed({ apiUrl: config.apiUrl, token }) ??
      pollingOnly("this data API serves no subscriptions");
    if (config.devToken) {
      const token = config.devToken;
      const api = portalApi(graphqlRequest(config.apiUrl, async () => token));
      render(
        <App
          api={api}
          identity={identityOf(token)}
          live={liveFeed(async () => token)}
          signInMail={config.signInMail}
        />,
      );
      return;
    }
    const auth = new Auth({
      authDomain: config.authDomain as string,
      clientId: config.clientId as string,
      redirectUri: `${window.location.origin}/`,
    });
    const url = new URL(window.location.href);
    if (await auth.completeSignIn(url)) {
      // Drop the code from the address, keeping the page.
      window.history.replaceState(null, "", `/${window.location.hash}`);
    }
    const token = await auth.idToken();
    if (!token) {
      render(<SignIn onSignIn={async () => window.location.assign(await auth.signInUrl())} />);
      return;
    }
    const api = portalApi(graphqlRequest(config.apiUrl, () => auth.idToken()));
    render(
      <App
        api={api}
        identity={identityOf(token)}
        live={liveFeed(() => auth.idToken())}
        signInMail={config.signInMail}
        onSignOut={() => window.location.assign(auth.signOutUrl())}
      />,
    );
  } catch (error) {
    render(
      <CenterCard>
        <Notice tone="bad" className="mb-5 text-left">
          {error instanceof Error ? error.message : String(error)}
        </Notice>
        <a href="/">Start again</a>
      </CenterCard>,
    );
  }
}

function SignIn({ onSignIn }: { onSignIn: () => void }) {
  return (
    <CenterCard>
      <p className="mt-2 mb-5 text-balance text-muted">
        Sign in with your agency email; a code is sent to it.
      </p>
      <Button variant="primary" size="lg" className="w-full" type="button" onClick={onSignIn}>
        Sign in
      </Button>
    </CenterCard>
  );
}

/** The sign-in and start-up error pages: one card in the middle, under the portal's name. */
function CenterCard({ children }: { children: ReactNode }) {
  return (
    <section className="mx-4 my-[10vh] rounded-xl border border-line bg-raised px-5 pt-7 pb-6 text-center shadow-card sm:mx-auto sm:my-[16vh] sm:max-w-[380px] sm:px-8 sm:pt-9 sm:pb-8">
      <Mark large />
      <h1 className="text-[1.375rem]">Quote Agent admin</h1>
      {children}
    </section>
  );
}

void start();
